import { useQuery, UseQueryResult } from "react-query";
import { UseQueryOptions } from "react-query/types/react/types";

import config from "@/config";
import { caseInsensitiveGet } from "@/core/object";
import { BrokerColors, BrokerSettings, defaultPaginationPayload } from "@/shared/domain";
import { getClaims, isExternalLogin, isPreviewUser } from "@/shared/hooks";

import { getSpApi, requestConfig } from "./apiRoot";
import { fOfficeSubOfficeSpGet } from "./authApi";
import * as brokerApi from "./brokerApi";
import { UrlManager } from "./UrlManager";

export const getBroker = async (officeId: string): Promise<MyBrokerApi.Broker> => {
    const axiosRequestConfig = requestConfig<Paths.ApiBroker$Id.Get.QueryParameters>({
        params: { light: true },
    });
    const { data } = await getSpApi().get<MyBrokerApi.Broker>(UrlManager.broker(officeId), axiosRequestConfig);
    return data;
};

export const useQueryBroker = (
    officeId: string,
    queryOptions: Omit<UseQueryOptions, "queryKey" | "queryFn"> = {},
): UseQueryResult<MyBrokerApi.Broker> => {
    return useQuery<MyBrokerApi.Broker>(["getBroker", officeId], () => brokerApi.getBroker(officeId), {
        enabled: !!officeId,
        ...queryOptions,
    });
};

export const getColors = async (officeId: string): Promise<BrokerColors> => {
    const { data } = await getSpApi().get<BrokerColors>(UrlManager.brokerColors(officeId));
    return data;
};

export const saveColors = async (request: { officeId: string; colors: BrokerColors }): Promise<void> => {
    const { officeId, colors } = request;
    await getSpApi().post<Paths.ApiBroker$OfficeIdColors.Post.Responses.$202>(
        UrlManager.brokerColors(officeId),
        colors,
    );
};

export const getBrokerSettings = async (): Promise<BrokerSettings> => {
    const { data } = await getSpApi().get<MyBrokerApi.BrokerSettings>(UrlManager.brokerSettings());
    return data;
};

export const filterOfficeIds = (officeIds: string[]): string[] => {
    const { OFFICE_IDS_FILTER: prefixes } = config;

    if (prefixes.length === 0) return officeIds;

    return officeIds.filter((officeId) => prefixes.some((prefix) => officeId.startsWith(prefix)));
};

export const getBrokers = async (): Promise<MyBrokerApi.BrokerPayload> => {
    // Get only available brokers
    let availableBrokerIds: string[];
    if (isPreviewUser() || isExternalLogin()) {
        const claims = getClaims();
        availableBrokerIds = [caseInsensitiveGet(claims, "officeId", "")];
    } else {
        const { res } = await fOfficeSubOfficeSpGet();
        availableBrokerIds = res.map((item) => item[0]);
    }
    // TODO: Ask BE to replace Paths.ApiBroker.Get.Parameters.Ids to type string[]
    // then just remove the join and use availableBrokerIds directly
    const filteredBrokersIds = filterOfficeIds(availableBrokerIds);
    if (filteredBrokersIds.length === 0) {
        return defaultPaginationPayload;
    }
    const axiosRequestConfig = requestConfig<Paths.ApiBroker.Get.QueryParameters>({
        params: { light: true, ids: filteredBrokersIds.join(",") },
    });

    const { data } = await getSpApi().get<MyBrokerApi.BrokerPayload>(UrlManager.brokers(), axiosRequestConfig);
    return data;
};

export const useQueryBrokers = (
    queryOptions: Omit<UseQueryOptions, "queryKey" | "queryFn"> = {},
): UseQueryResult<MyBrokerApi.BrokerPayload> => {
    return useQuery<MyBrokerApi.BrokerPayload>("getBrokers", () => brokerApi.getBrokers(), {
        placeholderData: defaultPaginationPayload,
        // It requires availableBrokerIds from fOfficeSubOfficeSpGet
        // Since it is not possible to get to add on queryKey the solution is to disable caching for now
        cacheTime: 0,
        ...queryOptions,
    });
};

const searchBrokerMapper = (source: MyBrokerApi.BrokerSearchResult): MyBrokerApi.Broker => {
    return {
        ...source,
        addressStringFrontEnd: source.addressStringFrontend,
        // add your extensions here
    };
};

export const addABroker = async (body: MyBrokerApi.AddBrokerRequest): Promise<number> => {
    const { status } = await getSpApi().post(UrlManager.addABroker(), body);
    return status;
};

export type BrokerFullTextSearchQueryParameters = Paths.ApiBrokerFullTextSearch.Get.Parameters.Query;
/**
 * Search for brokers
 * Returns any Broker containing at least 1 word either in name or address
 * @param query Word to search for
 */
export const searchBrokers = async (query: BrokerFullTextSearchQueryParameters): Promise<MyBrokerApi.Broker[]> => {
    const axiosRequestConfig = requestConfig({
        params: { query },
    });
    const { data } = await getSpApi().get<MyBrokerApi.Broker[]>(UrlManager.searchBroker(), axiosRequestConfig);
    return data.map((item) => searchBrokerMapper(item));
};
